import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { customAlphabet } from 'nanoid'
import { safeParseURLS } from '~/features/bots/utils/urls'
import {
  DeleteKnowledgeSourcesParams,
  DeleteWebPageSourceParams,
  DiscoverUrlsParams,
  DSData,
  GetKnowledgeSourceFileParams,
  KnowledgeSourceTypes,
  ListKnowledgeSourceParams,
  ListWebPageSourceParams,
  RenameKnowledgeSourceParams,
  RenameWebPageSourceParams,
  UploadDocumentSourcesParams,
  UploadRichTextSourcesParams,
  UploadWebPageSourceParams,
} from '~/features/knowledge-bases/types'
import { getQueryKey, useSuspenseInfiniteQuery, useSuspenseQuery } from '~/services'
import { getApiClient } from '~/stores'
import { listAll } from '~/utils'
import { startCrawlNewUrls, useCrawlTaskStore } from '../../bots/stores/useCrawlerStore'

const useListKnowledgeSources = (params: ListKnowledgeSourceParams) =>
  useSuspenseInfiniteQuery('workspaces_/$workspaceId_/bots_/$botId_/knowledgeBases_/$kbId_/sources/$tags/all', {
    ...params,
  })

export const useDsData = ({ workspaceId, botId, kbId }: { workspaceId: string; botId: string; kbId: string }) => {
  const { data: stateData, isPending } = useSuspenseQuery('workspaces_/$workspaceId_/bots_/$botId_/states/$name', {
    workspaceId,
    botId,
    name: 'dsData',
    type: 'bot',
  })
  const dsData: { [key: string]: DSData } = stateData.state?.payload?.[kbId] || {}
  const webPageDSData = Object.fromEntries(Object.entries(dsData).filter(([, value]) => value.type === 'web-page'))
  return { dsData: webPageDSData, isPending }
}

export const useKnowledgeSource = ({ workspaceId, botId, kbId, dsType }: ListKnowledgeSourceParams) => {
  const { isPending, dsData } = useDsData({ workspaceId, botId, kbId })
  const dsIds = Object.keys(dsData)
  const dsTypes = KnowledgeSourceTypes.filter((type) => type !== dsType)
  const query = useListKnowledgeSources({
    workspaceId,
    botId,
    kbId,
    tags: { dsType: dsType ? { not: dsTypes } : { not: 'web-page' }, dsId: { not: dsIds } },
  })
  const pending = isPending || query.isFetching

  return { pending, ...query }
}

export const useGetKnowledgeSourceFile = ({ workspaceId, botId, id }: GetKnowledgeSourceFileParams) => {
  return useSuspenseQuery('workspaces_/$workspaceId_/bots_/$botId_/files/$id', { workspaceId, botId, id })
}

export const useListWebPageSourceDsIds = ({ workspaceId, botId, kbId }: ListKnowledgeSourceParams) => {
  const { dsData } = useDsData({ workspaceId, botId, kbId })
  return Object.keys(dsData)
}

export const useWebPageSource = ({ workspaceId, botId, kbId, dsId }: ListWebPageSourceParams) => {
  const query = useListKnowledgeSources({ workspaceId, botId, kbId, tags: { dsId: dsId } })

  const { dsData } = useDsData({ workspaceId, botId, kbId })
  const title = dsData[dsId]?.title || 'Company Website'
  return { ...query, title }
}

export const useDeleteWebPageSource = ({ workspaceId, botId }: { workspaceId: string; botId: string }) => {
  const queryClient = useQueryClient()
  const { data: dsData } = useSuspenseQuery('workspaces_/$workspaceId_/bots_/$botId_/states/$name', {
    workspaceId,
    botId,
    name: 'dsData',
    type: 'bot',
  })

  return useMutation({
    mutationFn: async ({ workspaceId, botId, kbId, dsId }: DeleteWebPageSourceParams) => {
      const webPages = await listAll(
        getApiClient({ workspaceId, botId }).listFiles,
        { tags: { dsId: dsId } },
        ({ files }) => files
      )
      const webPageIds = webPages.map((file) => file.id)
      const deleteWebPages = Promise.all(
        webPageIds.map((id) => getApiClient({ workspaceId, botId }).deleteFile({ id }))
      )
      const newPayload = dsData.state?.payload || {}
      if (newPayload[kbId] && newPayload[kbId] !== undefined) {
        delete newPayload[kbId][dsId]

        const deleteWebPageSource = getApiClient({ workspaceId, botId }).setState({
          id: botId,
          type: 'bot',
          name: 'dsData',
          payload: newPayload,
        })

        return Promise.all([deleteWebPages, deleteWebPageSource])
      } else {
        return await deleteWebPages
      }
    },
    onSuccess: async (_, { workspaceId, botId, dsId, onSuccess: _onSuccess }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          predicate: (query) => query.queryHash.includes(dsId),
        }),
        queryClient.invalidateQueries({
          queryKey: getQueryKey('workspaces_/$workspaceId_/bots_/$botId_/states/$name', {
            workspaceId,
            botId,
            name: 'dsData',
          }),
        }),
      ])
      _onSuccess?.()
    },
  })
}

export const useDeleteKnowledgeSources = ({ workspaceId, botId }: { workspaceId: string; botId: string }) => {
  const queryClient = useQueryClient()
  const { data: dsData } = useSuspenseQuery('workspaces_/$workspaceId_/bots_/$botId_/states/$name', {
    workspaceId,
    botId,
    name: 'dsData',
    type: 'bot',
  })
  return useMutation({
    mutationFn: async ({ workspaceId, botId, kbId, ids, dsIds }: DeleteKnowledgeSourcesParams) => {
      const deleteKnowledgeSources = Promise.all(
        ids?.map((id) => getApiClient({ workspaceId, botId }).deleteFile({ id })) || []
      )
      if (!dsIds || dsIds.length === 0) {
        return await deleteKnowledgeSources
      }

      const newPayload = dsData.state?.payload || {}
      if (newPayload[kbId] && newPayload[kbId] !== undefined) {
        const webPages = await listAll(
          getApiClient({ workspaceId, botId }).listFiles,
          { tags: { dsId: dsIds } },
          ({ files }) => files
        )
        const webPageIds = webPages.map((file) => file.id)
        const deleteWebPages = Promise.all(
          webPageIds.map((id) => getApiClient({ workspaceId, botId }).deleteFile({ id }))
        )

        for (const dsId of dsIds) {
          delete newPayload[kbId][dsId]
        }
        const deleteWebPageDsData = getApiClient({ workspaceId, botId }).setState({
          id: botId,
          type: 'bot',
          name: 'dsData',
          payload: newPayload,
        })

        return Promise.all([deleteKnowledgeSources, deleteWebPages, deleteWebPageDsData])
      }
    },
    onSuccess: async (_, { workspaceId, botId, kbId, dsIds, onSuccess: _onSuccess }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          predicate: (query) =>
            !dsIds || dsIds.length === 0
              ? query.queryKey.includes(kbId)
              : dsIds.some((dsId) => query.queryHash.includes(dsId)),
        }),
        queryClient.invalidateQueries({
          queryKey: getQueryKey('workspaces_/$workspaceId_/bots_/$botId_/states/$name', {
            workspaceId,
            botId,
            name: 'dsData',
          }),
        }),
      ])
      _onSuccess?.()
    },
  })
}

// Helper function to get correct MIME type based on file extension, some files have an empty type (such as .md)
export const getContentType = (file: File): string => {
  if (file.type && file.type !== '') {
    return file.type
  }

  const extension = file.name.split('.').pop()?.toLowerCase()
  const mimeTypes: Record<string, string> = {
    pdf: 'application/pdf',
    html: 'text/html',
    htm: 'text/html',
    txt: 'text/plain',
    md: 'text/markdown',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    doc: 'application/msword',
  }

  return mimeTypes[extension || ''] || ''
}

export const useUploadDocumentSources = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ workspaceId, botId, kbId, files }: UploadDocumentSourcesParams) => {
      for (const file of files) {
        const contentType = getContentType(file)

        await getApiClient({ workspaceId, botId }).uploadFile({
          key: `${kbId}/${file.name}`,
          accessPolicies: ['public_content'],
          content: file as any,
          contentType: contentType,
          index: true,
          tags: {
            source: 'knowledge-base',
            kbId,
            dsType: 'document',
            title: file.name,
          },
        })
      }
    },
    onSuccess: async (_, { kbId, onSuccess: _onSuccess }) => {
      await queryClient.invalidateQueries({ predicate: (query) => query.queryKey.includes(kbId) })
      _onSuccess?.()
    },
  })
}

export const useUploadRichTextSource = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ workspaceId, botId, kbId, content, title }: UploadRichTextSourcesParams) => {
      const key = `rich-text-${customAlphabet('1234567890abcdef', 10)(10)}`
      return await getApiClient({ workspaceId, botId }).uploadFile({
        key: `${kbId}/${key}`,
        accessPolicies: ['public_content'],
        content,
        contentType: 'text/html',
        index: true,
        tags: {
          source: 'knowledge-base',
          kbId,
          dsType: 'rich-text',
          title: title || 'Rich Text Source',
        },
      })
    },
    onSuccess: async (_, { kbId, onSuccess: _onSuccess }) => {
      await queryClient.invalidateQueries({ predicate: (query) => query.queryKey.includes(kbId) })
      _onSuccess?.()
    },
  })
}

export const useUploadWebPageSource = ({ workspaceId, botId }: { workspaceId: string; botId: string }) => {
  const queryClient = useQueryClient()
  const crawlTaskStore = useCrawlTaskStore()
  const { data: dsData } = useSuspenseQuery('workspaces_/$workspaceId_/bots_/$botId_/states/$name', {
    workspaceId,
    botId,
    name: 'dsData',
    type: 'bot',
  })
  return useMutation({
    mutationFn: async ({ workspaceId, botId, kbId, dsId, ...props }: UploadWebPageSourceParams) => {
      const { pageUrl } = props
      const getTitle = (): string => {
        try {
          return new URL(pageUrl.includes('://') ? pageUrl : `https://${pageUrl}`).hostname
        } catch (error) {
          return pageUrl
        }
      }
      const crawlTask = startCrawlNewUrls({ workspaceId, botId, kbId, dsId, ...props })

      const payload = dsData.state?.payload || {}
      if (!Object.keys(payload[kbId] || {}).includes(dsId)) {
        const createdOn = DateTime.now().toMillis()
        const dsIdData: DSData = {
          createdOn,
          title: getTitle(),
          type: 'web-page',
          data: { websiteUrl: pageUrl, pages: [], indexingJobs: [] },
        }
        payload[kbId] = { ...payload[kbId], [dsId]: dsIdData }
        await getApiClient({ workspaceId, botId }).setState({
          id: botId,
          type: 'bot',
          name: 'dsData',
          payload,
        })
      }
      return await crawlTask
    },
    onSuccess: async (eventId, { workspaceId, botId, dsId, onSuccess: _onSuccess }) => {
      crawlTaskStore.trackCrawlEvent({ eventId, botId, workspaceId })

      await Promise.all([
        queryClient.invalidateQueries({
          predicate: (query) => query.queryHash.includes(dsId),
        }),
        queryClient.invalidateQueries({
          queryKey: getQueryKey('workspaces_/$workspaceId_/bots_/$botId_/states/$name', {
            workspaceId,
            botId,
            name: 'dsData',
          }),
        }),
      ])
      _onSuccess?.()
    },
  })
}

export const useRenameKnowledgeSource = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ workspaceId, botId, id, tags, name }: RenameKnowledgeSourceParams) => {
      const updatedTags = { ...tags, title: name }
      return await getApiClient({ workspaceId, botId }).updateFileMetadata({ id, tags: updatedTags })
    },
    onSuccess: async (_, { workspaceId, botId, id, kbId, onSuccess: _onSuccess }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getQueryKey('workspaces_/$workspaceId_/bots_/$botId_/files/$id', {
            workspaceId,
            botId,
            id,
          }),
        }),
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryHash.includes(botId) && query.queryHash.includes(workspaceId) && query.queryHash.includes(kbId),
        }),
      ])
      _onSuccess?.()
    },
  })
}

export const useRenameWebPageSource = ({ workspaceId, botId }: { workspaceId: string; botId: string }) => {
  const queryClient = useQueryClient()
  const { data: dsData } = useSuspenseQuery('workspaces_/$workspaceId_/bots_/$botId_/states/$name', {
    workspaceId,
    botId,
    name: 'dsData',
    type: 'bot',
  })

  return useMutation({
    mutationFn: async ({ workspaceId, botId, kbId, dsId, name }: RenameWebPageSourceParams) => {
      const payload = dsData.state?.payload || {}
      payload[kbId][dsId].title = name
      return await getApiClient({ workspaceId, botId }).setState({
        id: botId,
        type: 'bot',
        name: 'dsData',
        payload: payload,
      })
    },
    onSuccess: async (_, { workspaceId, botId, onSuccess: _onSuccess }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getQueryKey('workspaces_/$workspaceId_/bots_/$botId_/states/$name', {
            workspaceId,
            botId,
            name: 'dsData',
          }),
        }),
      ])
      _onSuccess?.()
    },
  })
}

export const useDiscoverUrls = () => {
  return useMutation({
    mutationFn: async ({ workspaceId, botId, url, includeGlobs = [], excludeGlobs = [] }: DiscoverUrlsParams) => {
      const urlToUse = url.includes('://') ? url : `https://${url}`

      const res = await getApiClient({ workspaceId, botId }).callAction({
        type: 'browser:discoverUrls',
        input: { url: urlToUse, onlyHttps: true, count: 1000, include: includeGlobs, exclude: excludeGlobs },
      })

      return safeParseURLS(res.output?.urls ?? [])
    },
    onSuccess: async (urls, { onSuccess: _onSuccess }) => {
      _onSuccess?.(urls)
    },
    onError: async (_, { onError: _onError }) => {
      _onError?.()
    },
  })
}
